import React from "react";
import { Card } from "@/components/ui/card";
import { User, MapPin, FileText, Calendar, Phone } from "lucide-react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";

export default function ApplicationInfoCard({ application }) {
  const { t } = useTranslation();

  return (
    <Card
      className="p-6 bg-white border border-stone-200 shadow-sm"
      data-testid="application-details"
    >
      <h2 className="text-xl font-manrope font-bold text-emerald-700 mb-6">
        {t("application.information")}
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          {/* Farmer Info */}
          <div className="flex items-start gap-3 mb-4">
            <User className="w-5 h-5 text-emerald-700 mt-0.5" />
            <div>
              <p className="text-xs text-emerald-700 uppercase tracking-wide mb-1">
                {t("application.farmer")}
              </p>
              <p className="text-sm font-medium text-slate-900">
                {application.farmer_name}
              </p>
              {application.farmer_phone && (
                <p className="text-xs text-slate-600 flex items-center gap-1 mt-1">
                  <Phone className="w-3 h-3" />
                  {application.farmer_phone}
                </p>
              )}
              {application.farmer_email && (
                <p className="text-xs text-slate-500">{application.farmer_email}</p>
              )}
            </div>
          </div>

          {/* Location Info */}
          <div className="flex items-start gap-3 mb-4">
            <MapPin className="w-5 h-5 text-emerald-700 mt-0.5" />
            <div>
              <p className="text-xs text-emerald-700 uppercase tracking-wide mb-1">
                {t("application.landLocationTitle")}
              </p>
              <div className="text-sm text-slate-900 space-y-0.5">
                <p>
                  <span className="text-emerald-600">
                    {t("newApplication.district")}:
                  </span>{" "}
                  {application.district}
                </p>
                {application.taluk && (
                  <p>
                    <span className="text-emerald-600">
                      {t("newApplication.taluk")}:
                    </span>{" "}
                    {application.taluk}
                  </p>
                )}
                {application.hobli && (
                  <p>
                    <span className="text-emerald-600">
                      {t("newApplication.hobli")}:
                    </span>{" "}
                    {application.hobli}
                  </p>
                )}
                <p>
                  <span className="text-emerald-600">
                    {t("newApplication.village")}:
                  </span>{" "}
                  {application.village}
                </p>
              </div>
              {application.location && (
                <p className="text-xs text-slate-500 mt-1 italic">
                  {application.location}
                </p>
              )}
            </div>
          </div>

          {/* Section and Circle Display */}
          {(application.section || application.circle) && (
            <div className="flex items-start gap-3 mt-4 pt-4 border-t border-stone-100">
              <FileText className="w-5 h-5 text-emerald-700 mt-0.5" />
              <div>
                <p className="text-xs text-emerald-700 uppercase tracking-wide mb-1">
                  {t("section.title")} & {t("circle.title")}
                </p>
                <div className="text-sm text-slate-900 space-y-0.5">
                  {application.section && (
                    <p>
                      <span className="text-emerald-600">
                        {t("section.assignedSection")}:
                      </span>{" "}
                      <span className="font-medium">
                        {t(`section.${application.section}`) || application.section}
                      </span>
                    </p>
                  )}
                  {application.circle && (
                    <p>
                      <span className="text-emerald-600">
                        {t("circle.assignedCircle")}:
                      </span>{" "}
                      <span className="font-medium">
                        {t(`circle.${application.circle}`) || application.circle}
                      </span>
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <div>
          {/* Land Area */}
          <div className="flex items-start gap-3 mb-4">
            <FileText className="w-5 h-5 text-emerald-700 mt-0.5" />
            <div>
              <p className="text-xs text-emerald-700 uppercase tracking-wide mb-1">
                {t("application.landArea")}
              </p>
              <p className="text-sm font-medium text-slate-900">
                {application.land_area} {t("application.acres")}
              </p>
            </div>
          </div>

          {/* Submitted Date */}
          <div className="flex items-start gap-3 mb-4">
            <Calendar className="w-5 h-5 text-emerald-700 mt-0.5" />
            <div>
              <p className="text-xs text-emerald-700 uppercase tracking-wide mb-1">
                {t("application.submittedOn")}
              </p>
              <p className="text-sm text-slate-900">
                {format(
                  new Date(application.created_at),
                  "MMMM dd, yyyy 'at' h:mm a"
                )}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Purpose */}
      <div className="mt-6 pt-6 border-t border-stone-200">
        <p className="text-xs text-emerald-700 uppercase tracking-wide mb-2">
          {t("application.purpose")}
        </p>
        <p className="text-sm text-slate-900">{application.purpose}</p>
      </div>
    </Card>
  );
}
